import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from '../components/GlassCard';
import FuturisticButton from '../components/FuturisticButton';

const { FiHeart, FiShield, FiZap, FiUsers, FiTarget, FiAward } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Patient-Centered',
      description: 'We believe every Australian patient deserves to understand their health information clearly and completely.'
    },
    {
      icon: FiShield,
      title: 'Privacy First',
      description: 'Your medical data is never stored. We comply with Australian Privacy Principles and use a zero-retention policy.'
    },
    {
      icon: FiZap,
      title: 'Cutting-Edge AI',
      description: 'Powered by advanced AI technology to provide accurate, helpful explanations of medical content for Australian users.'
    },
    {
      icon: FiUsers,
      title: 'Accessible Healthcare',
      description: 'Breaking down barriers between Australian patients and their health information through technology.'
    }
  ];

  const features = [
    'Multi-format support (PDF, images, text)',
    'Advanced OCR and text extraction',
    'AI-powered medical term translation',
    'Session-only data processing',
    'User-controlled API key security',
    'Mobile-friendly interface',
    'Australian Privacy Act compliance',
    'Australian emergency contact integration'
  ];

  const australianCompliance = [
    {
      title: 'Privacy Act 1988 Compliance',
      description: 'Full compliance with Australian Privacy Principles (APPs) ensuring your health information is protected.'
    },
    {
      title: 'Australian Consumer Law',
      description: 'Service provided under Australian Consumer Law with appropriate disclaimers and user protections.'
    },
    {
      title: 'Health Information Standards',
      description: 'Adheres to Australian health information handling standards and best practices.'
    }
  ];

  return (
    <div className="min-h-screen pt-20 sm:pt-24 md:pt-28 lg:pt-32 relative">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 terminal-text px-2">
              About MedExplainGPT Australia
            </h1>
            <p className="text-lg sm:text-xl text-green-200 mb-6 sm:mb-8 px-2">
              Empowering Australian patients with clear, understandable explanations of their medical reports, compliant with Australian privacy and health regulations
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <GlassCard className="p-6 sm:p-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 terminal-text">
                  Our Mission in Australia
                </h2>
                <p className="text-base sm:text-lg text-green-200 mb-4 sm:mb-6">
                  Healthcare communication shouldn't be a barrier to understanding your own health. MedExplainGPT bridges the gap between complex medical terminology and patient comprehension for Australians.
                </p>
                <p className="text-base sm:text-lg text-green-200 mb-4 sm:mb-6">
                  We believe that when Australian patients understand their medical reports, they become more engaged in their healthcare journey, ask better questions, and make more informed decisions within the Australian healthcare system.
                </p>
                <GlassCard className="p-4 sm:p-6 border border-green-400/30">
                  <SafeIcon icon={FiTarget} className="h-6 w-6 sm:h-8 sm:w-8 text-green-400 mb-3 sm:mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Our Goal</h3>
                  <p className="text-sm sm:text-base text-green-200">
                    To make medical information accessible, understandable, and actionable for every Australian patient, while maintaining compliance with Australian privacy laws and the highest standards of security.
                  </p>
                </GlassCard>
              </GlassCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <GlassCard className="p-6 sm:p-8 text-center" glow>
                <SafeIcon icon={FiAward} className="h-12 w-12 sm:h-16 sm:w-16 mb-4 sm:mb-6 text-green-400 mx-auto" />
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 terminal-text">Why It Matters in Australia</h3>
                <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base text-green-200">
                  <li>• 75% of Australian patients struggle to understand medical reports</li>
                  <li>• Clear communication improves treatment compliance</li>
                  <li>• Informed patients have better health outcomes</li>
                  <li>• Understanding reduces anxiety and fear</li>
                  <li>• Supports the Australian healthcare system</li>
                </ul>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 terminal-text">
              Our Core Values
            </h2>
            <p className="text-lg sm:text-xl text-green-200 max-w-2xl mx-auto">
              The principles that guide everything we do for Australian healthcare
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlassCard className="p-4 sm:p-6 text-center h-full">
                  <div className="bg-gradient-to-r from-green-400/20 to-green-500/20 w-12 h-12 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <SafeIcon icon={value.icon} className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-white mb-2 sm:mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm sm:text-base text-green-200">
                    {value.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Australian Compliance Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 terminal-text">
              Australian Compliance & Standards
            </h2>
            <p className="text-lg sm:text-xl text-green-200">
              Built to meet Australian legal and health information requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {australianCompliance.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlassCard className="p-4 sm:p-6 h-full">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-green-200">
                    {item.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 terminal-text">
              Platform Features
            </h2>
            <p className="text-lg sm:text-xl text-green-200">
              Built with the latest technology for Australian users
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlassCard className="flex items-center p-3 sm:p-4">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-3 sm:mr-4 flex-shrink-0"></div>
                  <span className="text-sm sm:text-base text-white">{feature}</span>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-6 sm:p-8" glow>
              <div className="text-center">
                <SafeIcon icon={FiShield} className="h-12 w-12 sm:h-16 sm:w-16 text-green-400 mx-auto mb-4 sm:mb-6" />
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6 terminal-text">
                  Your Privacy & Security in Australia
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 text-left">
                  <GlassCard className="p-4 sm:p-6">
                    <h3 className="font-semibold text-white mb-2 sm:mb-3">Zero Storage</h3>
                    <p className="text-green-200 text-xs sm:text-sm">
                      We never store your medical files or API keys. Everything is processed in real-time and discarded, complying with Australian Privacy Principles.
                    </p>
                  </GlassCard>
                  <GlassCard className="p-4 sm:p-6">
                    <h3 className="font-semibold text-white mb-2 sm:mb-3">HTTPS Encryption</h3>
                    <p className="text-green-200 text-xs sm:text-sm">
                      All data transmission is encrypted using industry-standard HTTPS protocols, meeting Australian cybersecurity standards.
                    </p>
                  </GlassCard>
                  <GlassCard className="p-4 sm:p-6">
                    <h3 className="font-semibold text-white mb-2 sm:mb-3">User Control</h3>
                    <p className="text-green-200 text-xs sm:text-sm">
                      You control your own OpenAI API key, ensuring complete privacy and data ownership under Australian law.
                    </p>
                  </GlassCard>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-6 sm:p-8" glow>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 terminal-text">
                Ready to Understand Your Health?
              </h2>
              <p className="text-lg sm:text-xl text-green-200 mb-6 sm:mb-8">
                Join thousands of Australian patients who have gained clarity about their medical reports
              </p>
              <div className="max-w-xs mx-auto">
                <FuturisticButton as={Link} to="/upload" size="lg">
                  <span className="hidden sm:inline">Get Started Today</span>
                  <span className="sm:hidden">Get Started</span>
                </FuturisticButton>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;